import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { toast } from "react-toastify";
import { FiPlus, FiEdit2, FiTrash2, FiEye, FiEyeOff, FiSearch, FiRefreshCw } from "react-icons/fi";
import { getAdmins, createAdmin, updateAdmin, deleteAdmin } from "../../services/adminService";

const AdminManagement = () => {
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [sortField, setSortField] = useState("createdAt");
  const [sortValue, setSortValue] = useState("DESC");
  const [status, setStatus] = useState("");
  const [searchKey, setSearchKey] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState("create"); // create, edit, view
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    username: "",
    password: "",
    status: true,
    isPrimary: true
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const queryClient = useQueryClient();

  // Fetch all admins (for client-side search)
  const { data: adminsData, isLoading, refetch } = useQuery(
    ["admins", sortField, sortValue, status],
    () => getAdmins({
      page: 1,
      limit: 1000, // Fetch all data for client-side search
      sortField,
      sortValue,
      status
      // Removed search parameter - will be handled client-side
    }),
    {
      onError: (error) => {
        console.error("Admins Error:", error);
        toast.error(error.message || "Failed to load admins");
      },
      refetchOnWindowFocus: false,
    }
  );

  // Create admin mutation
  const createAdminMutation = useMutation(
    (data) => createAdmin(data),
    {
      onSuccess: () => {
        toast.success("Admin created successfully");
        queryClient.invalidateQueries("admins");
        setShowModal(false);
        resetForm();
      },
      onError: (error) => {
        console.error("Create Admin Error:", error);
        toast.error(error.message || "Failed to create admin");
      },
    }
  );

  // Update admin mutation
  const updateAdminMutation = useMutation(
    (data) => updateAdmin(data),
    {
      onSuccess: () => {
        toast.success("Admin updated successfully");
        queryClient.invalidateQueries("admins");
        setShowModal(false);
        resetForm();
      },
      onError: (error) => {
        console.error("Update Admin Error:", error);
        toast.error(error.message || "Failed to update admin");
      },
    }
  );

  // Delete admin mutation
  const deleteAdminMutation = useMutation(
    (adminId) => deleteAdmin(adminId),
    {
      onSuccess: () => {
        toast.success("Admin deleted successfully");
        queryClient.invalidateQueries("admins");
      },
      onError: (error) => {
        console.error("Delete Admin Error:", error);
        toast.error(error.message || "Failed to delete admin");
      },
    }
  );

  // Handle the specific API response structure and apply client-side search
  const allAdmins = React.useMemo(() => {
    if (!adminsData || !adminsData.data) return [];
    return Array.isArray(adminsData.data.data) ? adminsData.data.data : [];
  }, [adminsData]);

  // Apply client-side search filtering
  const filteredAdmins = React.useMemo(() => {
    if (!searchKey.trim()) return allAdmins;
    
    const searchTerm = searchKey.toLowerCase().trim();
    return allAdmins.filter(admin => 
      admin.name?.toLowerCase().includes(searchTerm) ||
      admin.username?.toLowerCase().includes(searchTerm)
    );
  }, [allAdmins, searchKey]);

  // Apply client-side pagination to filtered results
  const admins = React.useMemo(() => {
    const startIndex = (page - 1) * limit;
    const endIndex = startIndex + limit;
    return filteredAdmins.slice(startIndex, endIndex);
  }, [filteredAdmins, page, limit]);

  const totalCount = filteredAdmins.length;
  const totalPages = Math.ceil(totalCount / limit);

  const resetForm = () => {
    setFormData({
      name: "",
      username: "",
      password: "",
      status: true,
      isPrimary: true
    });
    setSelectedAdmin(null);
    setShowPassword(false);
  };

  const handleCreate = () => {
    setModalType("create");
    resetForm();
    setShowModal(true);
  };

  const handleEdit = (admin) => {
    setModalType("edit");
    setSelectedAdmin(admin);
    setFormData({
      name: admin.name || "",
      username: admin.username || "",
      password: "",
      status: admin.status !== undefined ? admin.status : true,
      isPrimary: admin.isPrimary !== undefined ? admin.isPrimary : true
    });
    setShowModal(true);
  };

  const handleView = (admin) => {
    setModalType("view");
    setSelectedAdmin(admin);
    setFormData({
      name: admin.name || "",
      username: admin.username || "",
      password: "••••••••", // Masked password for view
      status: admin.status !== undefined ? admin.status : true,
      isPrimary: admin.isPrimary !== undefined ? admin.isPrimary : true
    });
    setShowModal(true);
  };

  const handleDelete = (admin) => {
    if (window.confirm(`Are you sure you want to delete admin "${admin.name}"?`)) {
      deleteAdminMutation.mutate(admin.id);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      if (modalType === "create") {
        const createData = {
          name: formData.name,
          username: formData.username,
          password: formData.password,
          isPrimary: formData.isPrimary
        };
        console.log('Creating admin with data:', createData);
        await createAdminMutation.mutateAsync(createData);
      } else if (modalType === "edit") {
        const updateData = {
          adminId: selectedAdmin.id
        };

        updateData.name = formData.name;
        updateData.username = formData.username;
        updateData.status = formData.status;
        updateData.isPrimary = formData.isPrimary;
        if (formData.password) updateData.password = formData.password;

        console.log('Updating admin with data:', updateData);
        console.log('Form data isPrimary:', formData.isPrimary, typeof formData.isPrimary);
        await updateAdminMutation.mutateAsync(updateData);
      }
    } catch (error) {
      // Error handling is done in mutation onError
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSort = (field) => {
    if (field === sortField) {
      setSortValue(sortValue === "ASC" ? "DESC" : "ASC");
    } else {
      setSortField(field);
      setSortValue("ASC");
    }
  };

  // Reset to page 1 when search changes
  React.useEffect(() => {
    setPage(1);
  }, [searchKey]);

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const getStatusBadge = (status) => {
    return (
      <span
        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${status
            ? "bg-green-100 text-green-800"
            : "bg-red-100 text-red-800"
          }`}
      >
        {status ? "Active" : "Inactive"}
      </span>
    );
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Admin Management</h1>
        <p className="text-gray-600">Manage admin users and their permissions</p>
      </div>

      {/* Filters and Actions */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="relative">
              <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
              <input
                type="text"
                placeholder="Search admins..."
                value={searchKey}
                onChange={(e) => setSearchKey(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent w-full sm:w-64"
              />
            </div>

            {/* Status Filter */}
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">All Status</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => refetch()}
              className="flex items-center px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              <FiRefreshCw size={16} className="mr-2" />
              Refresh
            </button>
            <button
              onClick={handleCreate}
              className="flex items-center px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded-lg transition-colors"
            >
              <FiPlus size={16} className="mr-2" />
              Add Admin
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort("name")}
                >
                  Name
                  {sortField === "name" && (
                    <span className="ml-1">{sortValue === "ASC" ? "↑" : "↓"}</span>
                  )}
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort("username")}
                >
                  Username
                  {sortField === "username" && (
                    <span className="ml-1">{sortValue === "ASC" ? "↑" : "↓"}</span>
                  )}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Role
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort("createdAt")}
                >
                  Created At
                  {sortField === "createdAt" && (
                    <span className="ml-1">{sortValue === "ASC" ? "↑" : "↓"}</span>
                  )}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {isLoading || !adminsData ? (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center">
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                      <span className="ml-2 text-gray-600">Loading admins...</span>
                    </div>
                  </td>
                </tr>
              ) : !Array.isArray(admins) || admins.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                    No admins found
                  </td>
                </tr>
              ) : (
                admins.map((admin) => (
                  <tr key={admin.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{admin.name}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{admin.username}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(admin.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${admin.isPrimary
                            ? "bg-purple-100 text-purple-800"
                            : "bg-gray-100 text-gray-800"
                          }`}
                      >
                        {admin.isPrimary ? "Admin" : "User"}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(admin.createdAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleView(admin)}
                          className="text-blue-600 hover:text-blue-900 p-1 rounded"
                          title="View"
                        >
                          <FiEye size={16} />
                        </button>
                        <button
                          onClick={() => handleEdit(admin)}
                          className="text-indigo-600 hover:text-indigo-900 p-1 rounded"
                          title="Edit"
                        >
                          <FiEdit2 size={16} />
                        </button>
                        <button
                          onClick={() => handleDelete(admin)}
                          className="text-red-600 hover:text-red-900 p-1 rounded"
                          title="Delete"
                        >
                          <FiTrash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => setPage(Math.min(totalPages, page + 1))}
                disabled={page === totalPages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing{" "}
                  <span className="font-medium">
                    {totalCount > 0 ? (page - 1) * limit + 1 : 0}
                  </span> to{" "}
                  <span className="font-medium">
                    {Math.min(page * limit, totalCount)}
                  </span>{" "}
                  of <span className="font-medium">{totalCount}</span> results
                  {searchKey && (
                    <span className="text-blue-600 ml-1">
                      (filtered from {allAdmins.length} total)
                    </span>
                  )}
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button
                    onClick={() => setPage(Math.max(1, page - 1))}
                    disabled={page === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => (
                    <button
                      key={pageNum}
                      onClick={() => setPage(pageNum)}
                      className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${pageNum === page
                          ? "z-10 bg-blue-50 border-blue-500 text-blue-600"
                          : "bg-white border-gray-300 text-gray-500 hover:bg-gray-50"
                        }`}
                    >
                      {pageNum}
                    </button>
                  ))}
                  <button
                    onClick={() => setPage(Math.min(totalPages, page + 1))}
                    disabled={page === totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  {modalType === "create" && "Create New Admin"}
                  {modalType === "edit" && "Edit Admin"}
                  {modalType === "view" && "View Admin"}
                </h3>
                <button
                  onClick={() => setShowModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Name
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    disabled={modalType === "view"}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                    required={modalType !== "view"}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Username (Email)
                  </label>
                  <input
                    type="email"
                    value={formData.username}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                    disabled={modalType === "view"}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                    required={modalType !== "view"}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Password
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      disabled={modalType === "view"}
                      className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                      required={modalType === "create"}
                      placeholder={modalType === "edit" ? "Leave empty to keep current password" : ""}
                    />
                    {modalType !== "view" && (
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      >
                        {showPassword ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                      </button>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value === "true" })}
                    disabled={modalType === "view"}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                  >
                    <option value={true}>Active</option>
                    <option value={false}>Inactive</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Role
                  </label>
                  <select
                    value={formData.isPrimary.toString()}
                    onChange={(e) => {
                      const newIsPrimary = e.target.value === "true";
                      console.log('Role changed:', e.target.value, '-> isPrimary:', newIsPrimary);
                      setFormData({ ...formData, isPrimary: newIsPrimary });
                    }}
                    disabled={modalType === "view"}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                  >
                    <option value="true">Admin</option>
                    <option value="false">User</option>
                  </select>
                </div>

                {modalType !== "view" && (
                  <div className="flex justify-end space-x-3 pt-4">
                    <button
                      type="button"
                      onClick={() => setShowModal(false)}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSubmitting ? "Saving..." : modalType === "create" ? "Create" : "Update"}
                    </button>
                  </div>
                )}

                {modalType === "view" && (
                  <div className="flex justify-end pt-4">
                    <button
                      type="button"
                      onClick={() => setShowModal(false)}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                    >
                      Close
                    </button>
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminManagement;
